import { useHistory, useLocation } from 'react-router-dom';
import languages from '../../utils/listOfLanguages';
import buildQuestionnaireLanguagePath from '../../helpers/questionnaires/buildQuestionnaireLanguagePath';
import { useTranslation } from '../../StateManagment/TranslationContext';

/**
 * Taalkiezer rechtsboven op de open vragenlijst.
 *
 * De taal zit in de url (segment 8), dezelfde plek die de deellink en de QR-code
 * vullen. De wissel doet een `replace` binnen dezelfde route, dus de pagina
 * remount niet en de al ingevulde antwoorden in de Responses-context blijven staan.
 *
 * Vertalen duurt merkbaar lang (een call per string), dus zolang de getoonde
 * teksten nog bij een andere taal horen draait er een spinner. Die vergelijking
 * dekt ook de periode vóór de vertaalronde, waarin de pagina eerst nog de opties
 * en matrixrijen ophaalt en er dus nog niets loopt om aan af te lezen.
 *
 * @param {object} props
 * @param {string} props.languageCode - Actieve taalcode uit de url ('' als de url er geen draagt)
 */
const LanguageSwitcher = ({ languageCode }) => {
    // Context
    const { language: translatedLanguage } = useTranslation();

    // Hooks
    const history = useHistory();
    const location = useLocation();

    const activeLanguage = languageCode || 'nl';
    const translating = activeLanguage !== (translatedLanguage || 'nl');
    // Een taal uit een oude link die niet (meer) in de lijst staat — Sranantongo,
    // ADR-055 — toont Nederlands. Zonder dit koos de keuzelijst zelf de eerste
    // optie en stond er 'AR' boven een Nederlandse tekst.
    const shownLanguage = languages.some(language => language.code === activeLanguage) ? activeLanguage : 'nl';

    const changeHandler = (e) => {
        const nextLanguageCode = e.target.value;
        if (!nextLanguageCode || nextLanguageCode === languageCode) return;

        const nextPath = buildQuestionnaireLanguagePath(location.pathname, nextLanguageCode);
        history.replace(`${nextPath}${location.search}`);
    };

    return (
        <div className="qo-language-switcher">
            {/* Eigen ring i.p.v. de gedeelde spinner-ripple: die tekent een haarlijn
                in een lichte kleur en is op dit formaat niet te zien. */}
            {translating && (
                <span
                    className="qo-language-spinner"
                    role="status"
                    aria-label="Vertalen… / Translating…"
                />
            )}
            {/* Blijft bruikbaar tijdens het vertalen: een misklik moet je meteen
                kunnen corrigeren in plaats van de ronde te moeten uitzitten. */}
            <select
                aria-label="Taal / Language"
                value={shownLanguage}
                onChange={changeHandler}
            >
                {languages.map(language => (
                    <option key={language.code} value={language.code}>
                        {language.flag} {language.code.toUpperCase()}
                    </option>
                ))}
            </select>
        </div>
    );
};

export default LanguageSwitcher;
